import { apiClient } from './client'

export type InviteRole = 'super_admin' | 'admin' | 'viewer'

export interface Invitation {
  id: string
  email: string
  role: InviteRole
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  invited_by_name: string | null
  expires_at: string
  created_at: string
  accepted_at: string | null
}

/** Result of creating or resending an invitation. */
export interface InvitationIssued {
  invitation: Invitation
  invite_url: string
  email_sent: boolean
  email_error: string | null
}

/** Invitation details visible to the invitee before accepting (no session required). */
export interface PublicInvitation {
  email: string
  role: InviteRole
  expires_at: string
}

/** React Query key for the pending-invitations list; invalidate after create/resend/revoke. */
export const INVITATIONS_QUERY_KEY = ['invitations'] as const

export const invitationApi = {
  /** Issue an invitation; 409 if the email is already invited or registered, 403 if the role is not permitted. */
  create: async (email: string, role: InviteRole): Promise<InvitationIssued> =>
    (await apiClient.post('/invitations/', { email, role })).data,
  /** List invitations, pending only by default. */
  list: async (status: 'pending' | 'all' = 'pending'): Promise<Invitation[]> =>
    (await apiClient.get('/invitations/', { params: { status } })).data,
  /** Issue a fresh link (and email) for a pending invitation; the previous link stops working. */
  resend: async (id: string): Promise<InvitationIssued> =>
    (await apiClient.post(`/invitations/${id}/resend`)).data,
  /** Cancel a pending invitation so its link can no longer be accepted. */
  revoke: async (id: string): Promise<void> => {
    await apiClient.delete(`/invitations/${id}`)
  },
  // Public (no session required)
  /** Look up the email/role behind an invite token before the invitee accepts it. */
  lookup: async (token: string): Promise<PublicInvitation> =>
    (await apiClient.get(`/auth/invitations/${encodeURIComponent(token)}`)).data,
  /** Accept an invitation, creating the account with the given name and password. */
  accept: async (
    token: string,
    data: { first_name: string; last_name: string; password: string }
  ): Promise<void> => {
    await apiClient.post(`/auth/invitations/${encodeURIComponent(token)}/accept`, data)
  },
}
